import React, { useState } from 'react';
import { auth, db } from './firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { logAuditEvent, logFailedLoginAttempt } from './auditLogService';
import { stampCompanyLastLogin } from './companyService';
import SignupFlow from './SignupFlow';
import { getBuildHash } from './releaseMatching';
import { getUserRoles } from './roleUtils';
import { RADIUS, SHADOW, COLORS } from './theme';
import { fnUrl } from './functionsBase';

// Every color below routes through COLORS (the shared theme.js palette) instead of the
// near-duplicate hex literals this file used to hand-roll independently — the very first screen
// every user sees was running its own almost entirely separate palette (COLORS was imported for
// RADIUS/SHADOW's sake but barely used for color at all). ${COLORS.rowHover} is deliberately left as a plain
// literal — it's the same light background this app's newer surfaces already use pervasively
// (InvoiceManagement.js, ReportBuilder.js, etc.), not an accidental near-duplicate of a real
// token, so swapping it would be a real (if subtle) redesign decision, not a like-for-like fix.

const FN_REQUEST_PASSWORD_RESET = fnUrl('requestPasswordReset');

function RoleLogin({ onLoginSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('login');
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const userDocRef = doc(db, 'appUsers', user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        const userRoles = getUserRoles(userData);
        const techName = userData.name || '';
        // Matches App.js's own resolveRoleAndName fallback exactly (['cpp'] only when the
        // field is missing entirely, not when it's present but genuinely empty — see
        // App.js's own comment on why: companyDeletion.js's purge cascade can leave a real
        // user with a real, empty companyIds array, and that person should see the "you're
        // not part of any company" screen, not cpp's data).
        const companyIds = Array.isArray(userData.companyIds)
          ? userData.companyIds
          : ['cpp'];

        localStorage.setItem('userRoles', JSON.stringify(userRoles));
        localStorage.setItem('technicianName', techName);
        localStorage.setItem('companyIds', JSON.stringify(companyIds));

        logAuditEvent({ action: 'login_success', category: 'security', details: { role: userRoles.join(', ') }, companyId: companyIds[0] || null });
        stampCompanyLastLogin(companyIds[0]);
        onLoginSuccess(user, userRoles);
      } else {
        if (email === 'admin@example.com') {
          localStorage.setItem('userRoles', JSON.stringify(['admin']));
          localStorage.setItem('technicianName', 'Admin');
          localStorage.setItem('companyIds', JSON.stringify(['cpp']));
          logAuditEvent({ action: 'login_success', category: 'security', details: { role: 'admin' }, companyId: 'cpp' });
          stampCompanyLastLogin('cpp');
          onLoginSuccess(user, ['admin']);
        } else {
          setError('User profile not found. Please ask admin to add you in Company Settings.');
        }
      }
    } catch (err) {
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        logFailedLoginAttempt(email);
      }
      if (err.code === 'auth/user-not-found') {
        setError('Email not found. Please ask admin to add you.');
      } else if (err.code === 'auth/wrong-password') {
        setError('Incorrect password.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else if (err.code === 'auth/user-disabled') {
        // Archiving a user now disables their Auth account server-side, so sign-in is rejected
        // with this code BEFORE any appUsers read — meaning the friendly "account archived" copy
        // App.js shows was unreachable from here, and the user saw the literal string
        // "Firebase: Error (auth/user-disabled)." instead.
        setError('This account has been archived. Contact your administrator if you believe this is a mistake.');
      } else {
        setError(err.message);
      }
    }

    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetMessage('');

    if (!/^\S+@\S+\.\S+$/.test(resetEmail)) {
      setResetError('That doesn\'t look like a valid email address.');
      return;
    }

    setResetLoading(true);

    try {
      // Cloud Function (functions/emailDeliveryService.js#requestPasswordReset) instead of
      // Firebase's own client-side sendPasswordResetEmail — that sends Firebase's fixed,
      // unbranded built-in email with no way to vary its content per company, the same reason
      // the admin-triggered reset (UserManagement.js) doesn't use it either. The function itself
      // never reveals whether the email matched a real account, so this always shows the same
      // message regardless.
      await fetch(FN_REQUEST_PASSWORD_RESET, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail })
      });
      setResetMessage('If an account exists for that email, a password reset link has been sent.');
    } catch (err) {
      setResetError('Something went wrong. Please try again.');
    }

    setResetLoading(false);
  };

  if (mode === 'signup') {
    return <SignupFlow onBackToLogin={() => setMode('login')} onSignupComplete={onLoginSuccess} />;
  }

  if (mode === 'reset') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: COLORS.rowHover }}>
        <div style={{ backgroundColor: COLORS.white, padding: '40px', borderRadius: RADIUS.md, boxShadow: SHADOW.raised, width: '100%', maxWidth: '400px' }}>
          <div style={{ marginBottom: '32px', textAlign: 'center' }}>
            <div style={{ backgroundColor: COLORS.sidebarBg, borderRadius: RADIUS.md, padding: '18px 20px', marginBottom: '12px' }}>
              <img src="/taskwrx-logo.png" alt="TaskWRX" style={{ width: '100%', maxWidth: '280px', height: 'auto', display: 'block', margin: '0 auto' }} />
            </div>
            <p style={{ margin: '0', fontSize: '14px', color: COLORS.textMuted }}>Reset your password</p>
          </div>

          <form onSubmit={handleResetPassword} data-testid="reset-form" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: COLORS.textMuted }}>Email</label>
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
                data-testid="reset-email"
                placeholder="your@email.com"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: RADIUS.md,
                  fontSize: '16px',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  backgroundColor: COLORS.rowHover
                }}
              />
            </div>

            {resetMessage && (
              <div style={{ padding: '12px', backgroundColor: COLORS.successBg, color: COLORS.success, borderRadius: RADIUS.md, fontSize: '12px', border: `1px solid ${COLORS.successBorder}` }}>
                {resetMessage}
              </div>
            )}
            {resetError && (
              <div style={{ padding: '12px', backgroundColor: COLORS.dangerBg, color: COLORS.danger, borderRadius: RADIUS.md, fontSize: '12px', border: `1px solid ${COLORS.dangerBorder}` }}>
                {resetError}
              </div>
            )}

            <button
              type="submit"
              disabled={resetLoading}
              data-testid="reset-submit"
              style={{
                padding: '12px',
                backgroundColor: COLORS.primary,
                color: COLORS.white,
                border: 'none',
                borderRadius: RADIUS.md,
                fontSize: '13px',
                fontWeight: '700',
                cursor: resetLoading ? 'not-allowed' : 'pointer',
                opacity: resetLoading ? 0.7 : 1
              }}
            >
              {resetLoading ? 'Sending…' : 'Send Reset Link'}
            </button>

            <button
              type="button"
              data-testid="back-to-login"
              onClick={() => {
                setMode('login');
                setResetMessage('');
                setResetError('');
              }}
              style={{
                padding: '8px',
                backgroundColor: 'transparent',
                color: COLORS.primary,
                border: 'none',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Back to Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: COLORS.rowHover }}>
      <div style={{ backgroundColor: COLORS.white, padding: '40px', borderRadius: RADIUS.md, boxShadow: SHADOW.raised, width: '100%', maxWidth: '400px' }}>
        <div style={{ marginBottom: '32px', textAlign: 'center' }}>
          <div style={{ backgroundColor: COLORS.sidebarBg, borderRadius: RADIUS.md, padding: '18px 20px' }}>
            <img src="/taskwrx-logo.png" alt="TaskWRX" style={{ width: '100%', maxWidth: '280px', height: 'auto', display: 'block', margin: '0 auto' }} />
          </div>
        </div>

        <form onSubmit={handleLogin} data-testid="login-form" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: COLORS.textMuted }}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="login-email"
              placeholder="your@email.com"
              style={{
                width: '100%',
                padding: '12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: RADIUS.md,
                fontSize: '16px',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                backgroundColor: COLORS.rowHover
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: COLORS.textMuted }}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="login-password"
              placeholder="••••••••"
              style={{
                width: '100%',
                padding: '12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: RADIUS.md,
                fontSize: '16px',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                backgroundColor: COLORS.rowHover
              }}
            />
          </div>

          {error && (
            <div style={{ padding: '12px', backgroundColor: COLORS.dangerBg, color: COLORS.danger, borderRadius: RADIUS.md, fontSize: '12px', border: `1px solid ${COLORS.dangerBorder}` }}>
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={() => {
              setMode('reset');
              setResetEmail(email);
              setError('');
            }}
            data-testid="forgot-password-link"
            style={{
              alignSelf: 'flex-end',
              padding: '0',
              backgroundColor: 'transparent',
              color: COLORS.primary,
              border: 'none',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Forgot password?
          </button>

          <button
            type="button"
            onClick={() => {
              setMode('signup');
              setError('');
            }}
            data-testid="signup-link"
            style={{
              alignSelf: 'flex-end',
              padding: '0',
              marginTop: '-8px',
              backgroundColor: 'transparent',
              color: COLORS.primary,
              border: 'none',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            New here? Create a company account
          </button>

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            style={{
              padding: '12px',
              backgroundColor: COLORS.primary,
              color: COLORS.white,
              border: 'none',
              borderRadius: RADIUS.md,
              fontSize: '13px',
              fontWeight: '700',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!loading) e.target.style.backgroundColor = COLORS.primaryHover;
            }}
            onMouseLeave={(e) => {
              if (!loading) e.target.style.backgroundColor = COLORS.primary;
            }}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>
      </div>
      {/* The BUILD, not a version. Nobody is signed in here, and the releases collection needs a
          signed-in reader, so the released version genuinely cannot be known at this point. This
          used to show a hand-maintained constant that had drifted ten patch releases behind what
          people were actually running -- worse than useless for a support question, because it
          answered confidently and wrongly. The build id is short, exact, and matches what the
          Deploy tab shows. */}
      <p style={{ marginTop: '16px', fontSize: '11px', color: COLORS.textFaint }}>
        TaskWRX{getBuildHash() ? ` · build ${getBuildHash().replace('main.', '')}` : ''}
      </p>
    </div>
  );
}

export default RoleLogin;